"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.genCkbBatchJumpBtcVirtualTx = exports.genCkbJumpBtcVirtualTx = void 0;
const base_1 = require("@ckb-lumos/base");
const error_1 = require("../error");
const utils_1 = require("../utils");
const rgbpp_1 = require("../utils/rgbpp");
const constants_1 = require("../constants");
const ckb_sdk_utils_1 = require("@nervosnetwork/ckb-sdk-utils");
/**
 * Generate the virtual ckb transaction for the jumping tx from CKB to BTC
 * @param collector The collector that collects CKB live cells and transactions
 * @param xudtTypeBytes The serialized hex string of the XUDT type script
 * @param fromCkbAddress The from ckb address who will use his private key to sign the ckb tx
 * @param toRgbppLockArgs The receiver rgbpp lock script args whose data structure is: out_index | bitcoin_tx_id
 * @param transferAmount The XUDT amount to be transferred
 * @param witnessLockPlaceholderSize The WitnessArgs.lock placeholder bytes array size and the default value is 3000(It can make most scenarios work properly)
 * @param ckbFeeRate The CKB transaction fee rate, default value is 1100
 * @param isMainnet
 */
const genCkbJumpBtcVirtualTx = (_a) => __awaiter(void 0, [_a], void 0, function* ({ collector, xudtTypeBytes, fromCkbAddress, toRgbppLockArgs, transferAmount, witnessLockPlaceholderSize, ckbFeeRate, }) {
    const isMainnet = fromCkbAddress.startsWith('ckb');
    const xudtType = base_1.blockchain.Script.unpack(xudtTypeBytes);
    if (!(0, utils_1.isTypeAssetSupported)(xudtType, isMainnet)) {
        throw new error_1.TypeAssetNotSupportedError('The type script asset is not supported now');
    }
    const fromLock = (0, ckb_sdk_utils_1.addressToScript)(fromCkbAddress);
    const xudtCells = yield collector.getCells({ lock: fromLock, type: xudtType });
    if (!xudtCells || xudtCells.length === 0) {
        throw new error_1.NoXudtLiveCellError('No rgbpp cells found with the xudt type script and the rgbpp lock args');
    }
    let { inputs, sumInputsCapacity, sumAmount } = collector.collectUdtInputs({
        liveCells: xudtCells,
        needAmount: transferAmount,
    });
    const rpbppCellCapacity = (0, utils_1.calculateRgbppCellCapacity)(xudtType);
    const outputsData = [(0, utils_1.append0x)((0, utils_1.u128ToLe)(transferAmount))];
    const outputs = [
        {
            lock: (0, rgbpp_1.genRgbppLockScript)(toRgbppLockArgs, isMainnet),
            type: xudtType,
            capacity: (0, utils_1.append0x)(rpbppCellCapacity.toString(16)),
        },
    ];
    let txFee = constants_1.MAX_FEE;
    const xudtCellCapacity = (0, utils_1.calculateUdtCellCapacity)(fromLock, xudtType);
    if (sumInputsCapacity < xudtCellCapacity + rpbppCellCapacity + constants_1.MIN_CAPACITY + txFee) {
        const emptyCells = yield collector.getCells({ lock: fromLock });
        if (!emptyCells || emptyCells.length === 0) {
            throw new error_1.NoLiveCellError('The address has no empty cells');
        }
        const { inputs: emptyInputs, sumInputsCapacity: sumEmptyCapacity } = collector.collectInputs(emptyCells, rpbppCellCapacity, txFee);
        inputs = [...emptyInputs, ...inputs];
        sumInputsCapacity += sumEmptyCapacity;
    }
    let changeCapacity = sumInputsCapacity - rpbppCellCapacity - txFee;
    if (sumAmount > transferAmount) {
        outputs.push({
            lock: fromLock,
            type: xudtType,
            capacity: (0, utils_1.append0x)(xudtCellCapacity.toString(16)),
        });
        outputsData.push((0, utils_1.append0x)((0, utils_1.u128ToLe)(sumAmount - transferAmount)));
        changeCapacity -= xudtCellCapacity;
    }
    outputs.push({
        lock: fromLock,
        capacity: (0, utils_1.append0x)(changeCapacity.toString(16)),
    });
    outputsData.push('0x');
    const cellDeps = [(0, constants_1.getXudtDep)(isMainnet)];
    const witnesses = inputs.map((_) => '0x');
    const ckbRawTx = {
        version: '0x0',
        cellDeps,
        headerDeps: [],
        inputs,
        outputs,
        outputsData,
        witnesses,
    };
    if (txFee === constants_1.MAX_FEE) {
        const txSize = (0, ckb_sdk_utils_1.getTransactionSize)(ckbRawTx) + (witnessLockPlaceholderSize !== null && witnessLockPlaceholderSize !== void 0 ? witnessLockPlaceholderSize : constants_1.RGBPP_TX_WITNESS_MAX_SIZE);
        const estimatedTxFee = (0, utils_1.calculateTransactionFee)(txSize, ckbFeeRate);
        const estimatedChangeCapacity = changeCapacity + (constants_1.MAX_FEE - estimatedTxFee);
        ckbRawTx.outputs[ckbRawTx.outputs.length - 1].capacity = (0, utils_1.append0x)(estimatedChangeCapacity.toString(16));
    }
    return ckbRawTx;
});
exports.genCkbJumpBtcVirtualTx = genCkbJumpBtcVirtualTx;
/**
 * Generate a virtual ckb transaction to realize a batch jump of assets from CKB to BTC
 * @param collector The collector that collects CKB live cells and transactions
 * @param xudtTypeBytes The serialized hex string of the XUDT type script
 * @param fromCkbAddress The from ckb address who will use his private key to sign the ckb tx
 * @param rgbppReceivers The rgbpp receiver list which include toRgbppLockArgs and transferAmount
 * @param witnessLockPlaceholderSize The WitnessArgs.lock placeholder bytes array size and the default value is 3000(It can make most scenarios work properly)
 * @param ckbFeeRate The CKB transaction fee rate, default value is 1100
 * @param isMainnet
 */
const genCkbBatchJumpBtcVirtualTx = (_b) => __awaiter(void 0, [_b], void 0, function* ({ collector, xudtTypeBytes, fromCkbAddress, rgbppReceivers, witnessLockPlaceholderSize, ckbFeeRate, }) {
    const isMainnet = fromCkbAddress.startsWith('ckb');
    const xudtType = base_1.blockchain.Script.unpack(xudtTypeBytes);
    if (!(0, utils_1.isTypeAssetSupported)(xudtType, isMainnet)) {
        throw new error_1.TypeAssetNotSupportedError('The type script asset is not supported now');
    }
    const fromLock = (0, ckb_sdk_utils_1.addressToScript)(fromCkbAddress);
    const xudtCells = yield collector.getCells({ lock: fromLock, type: xudtType });
    if (!xudtCells || xudtCells.length === 0) {
        throw new error_1.NoXudtLiveCellError('No rgbpp cells found with the xudt type script and the rgbpp lock args');
    }
    const sumTransferAmount = rgbppReceivers
        .map((receiver) => receiver.transferAmount)
        .reduce((prev, current) => prev + current, BigInt(0));
    let { inputs, sumInputsCapacity, sumAmount } = collector.collectUdtInputs({
        liveCells: xudtCells,
        needAmount: sumTransferAmount,
    });
    const rpbppCellCapacity = (0, utils_1.calculateRgbppCellCapacity)(xudtType);
    const sumRgbppCellCapacity = rpbppCellCapacity * BigInt(rgbppReceivers.length);
    const outputs = rgbppReceivers.map((receiver) => ({
        lock: (0, rgbpp_1.genRgbppLockScript)(receiver.toRgbppLockArgs, isMainnet),
        type: xudtType,
        capacity: (0, utils_1.append0x)(rpbppCellCapacity.toString(16)),
    }));
    const outputsData = rgbppReceivers.map((receiver) => (0, utils_1.append0x)((0, utils_1.u128ToLe)(receiver.transferAmount)));
    let txFee = constants_1.MAX_FEE;
    const xudtCellCapacity = (0, utils_1.calculateUdtCellCapacity)(fromLock, xudtType);
    if (sumInputsCapacity < xudtCellCapacity + sumRgbppCellCapacity + constants_1.MIN_CAPACITY + txFee) {
        const emptyCells = yield collector.getCells({ lock: fromLock });
        if (!emptyCells || emptyCells.length === 0) {
            throw new error_1.NoLiveCellError('The address has no empty cells');
        }
        const { inputs: emptyInputs, sumInputsCapacity: sumEmptyCapacity } = collector.collectInputs(emptyCells, rpbppCellCapacity, txFee);
        inputs = [...emptyInputs, ...inputs];
        sumInputsCapacity += sumEmptyCapacity;
    }
    let changeCapacity = sumInputsCapacity - sumRgbppCellCapacity - txFee;
    if (sumAmount > sumTransferAmount) {
        outputs.push({
            lock: fromLock,
            type: xudtType,
            capacity: (0, utils_1.append0x)(xudtCellCapacity.toString(16)),
        });
        outputsData.push((0, utils_1.append0x)((0, utils_1.u128ToLe)(sumAmount - sumTransferAmount)));
        changeCapacity -= xudtCellCapacity;
    }
    outputs.push({
        lock: fromLock,
        capacity: (0, utils_1.append0x)(changeCapacity.toString(16)),
    });
    outputsData.push('0x');
    const cellDeps = [(0, constants_1.getXudtDep)(isMainnet)];
    const witnesses = inputs.map((_) => '0x');
    const ckbRawTx = {
        version: '0x0',
        cellDeps,
        headerDeps: [],
        inputs,
        outputs,
        outputsData,
        witnesses,
    };
    if (txFee === constants_1.MAX_FEE) {
        const txSize = (0, ckb_sdk_utils_1.getTransactionSize)(ckbRawTx) + (witnessLockPlaceholderSize !== null && witnessLockPlaceholderSize !== void 0 ? witnessLockPlaceholderSize : constants_1.RGBPP_TX_WITNESS_MAX_SIZE);
        const estimatedTxFee = (0, utils_1.calculateTransactionFee)(txSize, ckbFeeRate);
        const estimatedChangeCapacity = changeCapacity + (constants_1.MAX_FEE - estimatedTxFee);
        ckbRawTx.outputs[ckbRawTx.outputs.length - 1].capacity = (0, utils_1.append0x)(estimatedChangeCapacity.toString(16));
    }
    return ckbRawTx;
});
exports.genCkbBatchJumpBtcVirtualTx = genCkbBatchJumpBtcVirtualTx;
